import {
  DatagridConfigurable,
  List,
  TextField,
  TopToolbar,
  SelectColumnsButton,
  NumberField,
  SelectInput,
  required,
  useSidebarState,
  TextInput,
  FunctionField,
} from "react-admin";

import { ListNoResultsWithFilters } from "@/components/ListNoResultsWithFilters";
import { SimpleBoxLoader } from "@/components/loader";
import { FILTER_BASE_PROPS } from "@/constants/filters";
import { MODULE_NAMES } from "@/constants/modules";
import { BaseDateInput } from "@/inputs/BaseDateInput";
import { ReferenceAutoCompleteArrayInput } from "@/inputs/ReferenceAutoCompleteArrayInput";
import { ClientAppInput } from "@/modules/clientApps";

import { useRebillsGroups } from "../api/";

import {
  COST_FIELDS,
  DEFAULT_DATE_START,
  DEFAULT_DATE_END,
  REBILLS_TIMEZONES_CHOICES,
  REBILLS_TYPE_CHOICES,
  TEXT_FIELDS,
} from "./constants";

const COUNTRY_LIMIT_TAGS = 2;
const ROI_DECIMALS = 2;

const ListActions = () => (
  <TopToolbar>
    <SelectColumnsButton />
  </TopToolbar>
);

export const RebillsList = () => {
  const { data: groupsList = [], isLoading: rebillsGroupsLoading } =
    useRebillsGroups();
  const [open] = useSidebarState();

  const DEFAULT_FILTERS = {
    group: groupsList[0],
    type: REBILLS_TYPE_CHOICES[0].id,
    date_start: DEFAULT_DATE_START,
    date_end: DEFAULT_DATE_END,
    timezone: REBILLS_TIMEZONES_CHOICES[0].id,
  };

  if (rebillsGroupsLoading) {
    return <SimpleBoxLoader />;
  }

  return (
    <List
      filters={[
        <SelectInput
          key="group"
          source="group"
          choices={groupsList}
          validate={[required()]}
          {...FILTER_BASE_PROPS}
        />,
        <SelectInput
          key="type"
          source="type"
          choices={REBILLS_TYPE_CHOICES}
          validate={[required()]}
          {...FILTER_BASE_PROPS}
        />,
        <BaseDateInput
          key="dateStart"
          label="Date from"
          source="date_start"
          isRequired
          {...FILTER_BASE_PROPS}
        />,
        <BaseDateInput
          key="dateEnd"
          label="Date to"
          source="date_end"
          isRequired
          {...FILTER_BASE_PROPS}
        />,
        <ReferenceAutoCompleteArrayInput
          key="country"
          source="country"
          label="Countries"
          optionValue="code"
          module={MODULE_NAMES.COUNTRIES}
          disableCloseOnSelect
          limitTags={COUNTRY_LIMIT_TAGS}
          {...FILTER_BASE_PROPS}
        />,
        <SelectInput
          key="timezone"
          source="timezone"
          choices={REBILLS_TIMEZONES_CHOICES}
          validate={[required()]}
          {...FILTER_BASE_PROPS}
        />,
        <ClientAppInput
          key="clientApp"
          source="clientApp"
          {...FILTER_BASE_PROPS}
        />,
        <TextInput
          key="tracker_campaign_id"
          source="tracker_campaign_id"
          label="Tracker Campaign ID"
          {...FILTER_BASE_PROPS}
        />,
      ]}
      filterDefaultValues={DEFAULT_FILTERS}
      actions={<ListActions />}
      pagination={false}
      storeKey={false}
    >
      <DatagridConfigurable
        bulkActionButtons={false}
        className={`datagrid-scroll ${!open ? "collapsed" : ""}`}
        empty={<ListNoResultsWithFilters defaultFilters={DEFAULT_FILTERS} />}
      >
        {TEXT_FIELDS.map((field) => (
          <TextField key={field} source={field} />
        ))}
        <FunctionField
          label="profit"
          source="profit"
          render={(record: { profit: number }) =>
            record.profit ? (
              <span
                style={{ whiteSpace: "nowrap" }}
              >{`${record.profit} $`}</span>
            ) : (
              record.profit
            )
          }
        />
        <FunctionField
          label="ROI"
          source="ROI"
          render={(record: { ROI: number }) =>
            `${record.ROI.toFixed(ROI_DECIMALS)}%`
          }
        />
        <NumberField
          source="CPT"
          title="CPT"
          options={{ style: "currency", currency: "USD" }}
        />
        {COST_FIELDS.map((field) => (
          <NumberField
            key={field}
            source={field}
            options={{ style: "currency", currency: "USD" }}
          />
        ))}
      </DatagridConfigurable>
    </List>
  );
};
